// plan-16 S06: video player modal
// Full-viewport modal at z-layer 90 (modal-player per §42)
// Features: close, fullscreen, PiP, download, resume, drone attenuation

import { on } from '../../core/bus'
import { load, set } from '../../core/save'

let modal: HTMLElement | null = null
let video: HTMLVideoElement | null = null
let currentFileId = ''
let firstOpenThisSession = true

export function mountPlayer(): void {
  on('filesystem:play', (payload) => {
    openPlayer(payload.path, payload.displayName)
  })
}

function openPlayer(urlPath: string, displayName: string): void {
  // Safety: only allow same-origin /library/ paths
  if (!urlPath.startsWith('/library/')) return

  closeModal() // close any existing modal

  currentFileId = urlPath

  const s = load()
  const saved = s.playerState[urlPath]

  // Diegetic friction: first video open this session
  if (firstOpenThisSession) {
    firstOpenThisSession = false
    const history = document.querySelector('.vt-history') as HTMLElement | null
    if (history) {
      const line = document.createElement('div')
      line.className = 'vt-line-oracle'
      line.style.color = 'var(--vt-green-dim)'
      line.textContent = '> archive surface. era: probable.'
      history.appendChild(line)
      scheduleAnchor()
    }
  }

  // Drone attenuation
  attenuateDrone(true)

  // Build modal
  modal = document.createElement('div')
  modal.className = 'vt-player-backdrop'

  const surface = document.createElement('div')
  surface.className = 'vt-player-surface'

  // Header
  const header = document.createElement('div')
  header.className = 'vt-player-header'

  const title = document.createElement('span')
  title.className = 'vt-player-title'
  title.textContent = displayName
  header.appendChild(title)

  const closeBtn = document.createElement('button')
  closeBtn.className = 'vt-player-close'
  closeBtn.textContent = '[ close ✕ ]'
  closeBtn.setAttribute('aria-label', 'close player')
  closeBtn.addEventListener('click', () => closeModal())
  header.appendChild(closeBtn)

  surface.appendChild(header)

  // Video element
  video = document.createElement('video')
  video.className = 'vt-player-video'
  video.controls = true
  video.preload = 'metadata'
  video.playsInline = true
  video.src = urlPath
  if (saved) {
    video.volume = saved.volume
    if (saved.lastPos > 8) {
      video.currentTime = saved.lastPos
    }
  }
  surface.appendChild(video)

  // Footer chip-row
  const footer = document.createElement('div')
  footer.className = 'vt-player-footer'

  const fullscreenBtn = document.createElement('button')
  fullscreenBtn.className = 'vt-player-chip'
  fullscreenBtn.textContent = '[ fullscreen ]'
  fullscreenBtn.addEventListener('click', () => {
    if (surface.requestFullscreen) surface.requestFullscreen()
    else if ((surface as any).webkitRequestFullscreen) (surface as any).webkitRequestFullscreen()
  })
  footer.appendChild(fullscreenBtn)

  const pipBtn = document.createElement('button')
  pipBtn.className = 'vt-player-chip'
  pipBtn.textContent = '[ pip ]'
  pipBtn.addEventListener('click', async () => {
    try {
      if (video && document.pictureInPictureEnabled) {
        await video.requestPictureInPicture()
      }
    } catch {
      // PiP not supported or denied
    }
  })
  footer.appendChild(pipBtn)

  const downloadBtn = document.createElement('a')
  downloadBtn.className = 'vt-player-chip'
  downloadBtn.textContent = '[ download ]'
  downloadBtn.href = urlPath
  downloadBtn.download = displayName + '.mp4'
  footer.appendChild(downloadBtn)

  // Duration chip (informational)
  const durationChip = document.createElement('span')
  durationChip.className = 'vt-player-chip vt-player-info'
  durationChip.textContent = '[ ◇ loading... ]'
  video.addEventListener('loadedmetadata', () => {
    if (video) {
      const dur = video.duration
      if (isFinite(dur)) {
        const m = Math.floor(dur / 60)
        const s = Math.floor(dur % 60)
        durationChip.textContent = `[ ◇ ${m}:${String(s).padStart(2, '0')} ]`
      }
    }
  })
  footer.appendChild(durationChip)

  surface.appendChild(footer)
  modal.appendChild(surface)
  document.body.appendChild(modal)

  // Keyboard shortcuts
  document.addEventListener('keydown', onPlayerKey)

  // Backdrop click closes
  modal.addEventListener('click', (e) => {
    if (e.target === modal) closeModal()
  })

  // Track position for resume
  video.addEventListener('timeupdate', () => {
    // Throttled — only save every 5s
  })
}

function closeModal(): void {
  if (video && currentFileId) {
    const pos = video.currentTime
    const vol = video.volume
    const s = load()
    const ps = { ...s.playerState }
    if (pos > 8) {
      ps[currentFileId] = { lastPos: pos, volume: vol }
    } else {
      delete ps[currentFileId]
    }
    set('playerState', ps)
  }

  if (video) {
    video.pause()
    video.src = ''
  }

  document.removeEventListener('keydown', onPlayerKey)
  modal?.remove()
  modal = null
  video = null
  currentFileId = ''

  // Restore drone
  attenuateDrone(false)
}

function onPlayerKey(e: KeyboardEvent): void {
  if (!video) return

  switch (e.key) {
    case 'Escape':
      closeModal()
      e.preventDefault()
      break
    case ' ':
      e.preventDefault()
      if (video.paused) video.play().catch(() => {})
      else video.pause()
      break
    case 'ArrowLeft':
      e.preventDefault()
      video.currentTime = Math.max(0, video.currentTime - 10)
      break
    case 'ArrowRight':
      e.preventDefault()
      video.currentTime = Math.min(video.duration || 0, video.currentTime + 10)
      break
    case 'ArrowUp':
      e.preventDefault()
      video.volume = Math.min(1, video.volume + 0.1)
      break
    case 'ArrowDown':
      e.preventDefault()
      video.volume = Math.max(0, video.volume - 0.1)
      break
    case 'f':
      if (modal?.querySelector('.vt-player-surface')?.requestFullscreen) {
        ;(modal.querySelector('.vt-player-surface') as HTMLElement).requestFullscreen()
      }
      break
    case 'p':
      try {
        if (document.pictureInPictureEnabled && video) {
          video.requestPictureInPicture().catch(() => {})
        }
      } catch { /* noop */ }
      break
    case 'm':
      video.muted = !video.muted
      break
  }
}

function attenuateDrone(attenuate: boolean): void {
  // Attenuate drone by ~6 dB while player is open
  try {
    import('../../audio/drone').then(({ setPlayerAttenuation }) => {
      setPlayerAttenuation(attenuate)
    }).catch(() => {})
  } catch { /* noop */ }
}

function scheduleAnchor(): void {
  const fn = (window as unknown as Record<string, unknown>).__vtScheduleAnchor
  if (typeof fn === 'function') (fn as () => void)()
}
